import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { getRanaApiV1 } from "@nens/rana/api-v1";
import { RanaTable, TableRowProps } from "@nens/ranalib";

import { RanaLink } from "@/components/rana-link";
import { useTenantActionAllowed } from "@/hooks/use-action-allowed-in-tenant";
import { useIconSrc } from "@/hooks/use-icon-src";
import { ProfileStack } from "@/lib/profile";
import { nameColumnString } from "@/translations";
import { getPrettyTime, getPrettyTimeFromNow } from "@/utils/get-pretty-time";

import { useTenantProjectListData } from "./use-tenant-project-list-data";

interface TenantProjectListTableProps extends ReturnType<typeof useTenantProjectListData> {
  getRowProps: (project: ProjectRead) => Omit<TableRowProps, "rowId">;
  hoveredProjectId?: string | null;
  setHoveredProjectId?: (projectId: string | null) => void;
  selectedProjectIds?: string[];
}

export function TenantProjectListTable({
  hasProjects,
  projectsOnPage,
  orderings,
  setOrdering,
  isLoading,
  searchString,
  setSearchString,
  page,
  numPages,
  limit,
  setPageSize,
  setPage,
  contributorDropdown,
  getRowProps,
  hoveredProjectId,
  setHoveredProjectId,
  selectedProjectIds = [],
}: TenantProjectListTableProps) {
  const { canCreateNewProject, canReadAllProjects } = useTenantActionAllowed();
  const intl = useIntl();
  const icon = useIconSrc("project");

  const EmptyTableContent = () => {
    if (hasProjects) {
      return (
        <FormattedMessage
          id="table.empty.no_projects_with_filters"
          defaultMessage="No projects with these filters."
        />
      );
    }

    if (!canReadAllProjects) {
      return (
        <FormattedMessage
          id="table.empty.not_been_added_to_any_projects"
          defaultMessage="You have not been added to any projects yet."
        />
      );
    }

    return (
      <>
        <FormattedMessage id="table.empty.no_projects" defaultMessage="No projects yet." />

        {canCreateNewProject && (
          <RanaLink
            color="brand.analytic"
            textDecor="underline"
            to={`/${getRanaApiV1().tenantId}/projects-new`}
          >
            <FormattedMessage
              id="button.create_first_project"
              defaultMessage="Create your first project"
            />
          </RanaLink>
        )}
      </>
    );
  };

  return (
    <RanaTable
      columnDefinitions={[
        {
          width: "table_cell",
          title: nameColumnString(intl),
          orderingField: "name",
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "column.code",
            defaultMessage: "Code",
          }),
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "column.contributors",
            defaultMessage: "Contributors",
          }),
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "column.latest_activity",
            defaultMessage: "Latest activity",
          }),
          orderingField: "lastActivity",
        },
        {
          width: "table_cell_xs",
          title: intl.formatMessage({
            id: "column.created_at",
            defaultMessage: "Created at",
          }),
          orderingField: "createdAt",
        },
      ]}
      ordering={orderings}
      setOrdering={setOrdering}
      isSkeleton={isLoading}
      tableDataIds={projectsOnPage.map((project) => project.id)}
      emptyElement={
        <RanaTable.Empty icon={icon}>
          <EmptyTableContent />
        </RanaTable.Empty>
      }
    >
      <RanaTable.Filters
        search={{
          placeholder: intl.formatMessage({
            id: "search.project",
            defaultMessage: "Find project",
          }),
          searchString,
          setSearchString,
        }}
        dropdowns={[contributorDropdown]}
      />

      <RanaTable.Container maxH="lg" overflowY="auto">
        <RanaTable.Header />
        <RanaTable.Body>
          {projectsOnPage.map((project) => {
            const isHovered = hoveredProjectId === project.id;
            const isSelected = selectedProjectIds.includes(project.id);
            return (
              <RanaTable.Row
                key={project.id}
                rowId={project.id}
                {...getRowProps(project)}
                isHovered={isHovered}
                isSelected={isSelected}
                onMouseEnter={() => project.location && setHoveredProjectId?.(project.id)}
                onMouseLeave={() => project.location && setHoveredProjectId?.(null)}
              >
                <RanaTable.Cell>{project.name}</RanaTable.Cell>
                <RanaTable.Cell>{project.code}</RanaTable.Cell>
                <RanaTable.Cell>
                  <ProfileStack users={project.contributors} />
                </RanaTable.Cell>
                <RanaTable.Cell title={getPrettyTime(project.lastActivity)}>
                  {getPrettyTimeFromNow(project.lastActivity)}
                </RanaTable.Cell>
                <RanaTable.Cell title={getPrettyTime(project.createdAt)}>
                  {getPrettyTimeFromNow(project.createdAt)}
                </RanaTable.Cell>
              </RanaTable.Row>
            );
          })}
        </RanaTable.Body>
      </RanaTable.Container>

      <RanaTable.Pagination
        limit={limit}
        setPageSize={setPageSize}
        page={page}
        numPages={numPages}
        setPage={setPage}
      />
    </RanaTable>
  );
}
